import { MultiCountryData } from '../../../../create-survey.service';
import { EachLanguage } from '../../../interfaces/common-service.interface';
import {
  FeasibilityRangeQuota,
  FeasibilityResponse,
} from '../../../../..//shared/interfaces/feasibility.interface';
import {
  MarginOverride,
  Qualification,
} from '../../../../../shared/interfaces/survey.interface';
import { SelectedCountryInterface } from '../../../../../shared/interfaces/create-surveys.interface';
import {
  McPreDraftSurvey,
  McSurveyRequest,
} from '../interface/multi-country.interface';
import { DEFAULT } from '../../../../../shared/constants/defaults';

export class SurveyRequestMapper {
  constructor(
    private readonly _selectedCountryData: MultiCountryData,
    private readonly _buyerCompany: number,
    private readonly _userId: number,
    private readonly _isServiceBuyer: boolean
  ) {}

  public transform(): McSurveyRequest[] {
    const { selectedCountries } = this._selectedCountryData;
    return selectedCountries.reduce((acc, selectedCountry) => {
      const { lang } = selectedCountry;
      const selectedLanguages = this._selectedLangs(lang);
      return [
        ...acc,
        ...selectedLanguages.map((eachLanguage) =>
          this._mapper(selectedCountry, eachLanguage)
        ),
      ];
    }, <McSurveyRequest[]>[]);
  }

  private _selectedLangs(languages: EachLanguage[]): EachLanguage[] {
    if (this._selectedCountryData.selectedCountries.length > 1) {
      return languages;
    }

    return languages.filter((eachLanguage) =>
      this._selectedCountryData.selectedLanguages.some(
        (selectedLang) => selectedLang.id === eachLanguage.id
      )
    );
  }

  private _mapper(
    selectedCountry: SelectedCountryInterface,
    language: EachLanguage
  ): McSurveyRequest {
    const {
      surveyTitle: survey_title,
      clickBalancing: click_balancing,
      surveyCategoryCode: survey_category_code,
      completesRequired: completes_required,
      expectedIr: expected_ir,
      expectedLoi: expected_loi,
      fieldTime: field_time,
      offerPrice: offer_price,
      payloadQualifications = [],
      quotas: payloadQuotas = [],
    } = this._selectedCountryData;
    const { short_Code: country_code, name: country_name } = selectedCountry;
    const {
      transalte_code: language_code,
      short_code: language_short_code,
      name: language_name,
    } = language;
    const aida = DEFAULT.AIDA_PAYLOAD;
    return {
      buyer_company: this._buyerCompany,
      buyer_user_id: this._userId,
      country_name,
      country_code,
      language_name,
      language_code,
      language_short_code,
      survey_title,
      click_balancing,
      survey_category_code,
      survey_localization: this._localization(language_code, country_code),
      completes_required,
      expected_ir,
      expected_loi,
      field_time,
      source: this._source(),
      qualifications: payloadQualifications as Qualification[],
      quotas: payloadQuotas as FeasibilityRangeQuota[],
      aida,
      ...(this._isServiceBuyer && {
        offer_price: this._offerPrice(),
        margin_override: this._marginOverride(),
      }),
    };
  }

  private _source() {
    return 'platform';
  }

  private _localization(languageCode: string, countryCode: string) {
    return `${languageCode}_${countryCode}`;
  }

  private _offerPrice(): number {
    const { offerPrice = 0 } = this._selectedCountryData;
    return Number(offerPrice);
  }

  private _marginOverride(): MarginOverride {
    const { buyerMargin: margin = 0 } = this._selectedCountryData;
    return {
      enable: true,
      margin,
    };
  }
}

export class MultiCountryPreDraftDataMapper {
  constructor(
    private readonly _surveyRequest: McSurveyRequest,
    private readonly _feasibilityResponse: FeasibilityResponse,
    private readonly _isServiceBuyer: boolean = false
  ) {}

  public transform(): McPreDraftSurvey {
    const { currency_fx: { symbol: currency_symbol = '$' } = {} } =
      this._feasibilityResponse;
    return {
      checked: !!this._surveyRequest.checked,
      feasibility: this._feasibility(),
      cpi: this._cpi(),
      currency_symbol,
      ...this._surveyRequest,
    };
  }

  private _feasibility(): number {
    const { total_feasibility, max_feasibility } = this._feasibilityResponse;
    return Math.min(total_feasibility, max_feasibility);
  }

  private _cpi(): number {
    const { offer_price = 0 } = this._surveyRequest;

    if (this._isServiceBuyer) {
      return offer_price;
    }

    const { feasibility_cpi, recommended_cpi = 0 } = this._feasibilityResponse;

    if (feasibility_cpi) {
      return feasibility_cpi;
    }

    return recommended_cpi;
  }
}
